import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
} from '@react-pdf/renderer';
import { migrosLogoDataUrl } from './migros-logo-data';
import type { PilotRunPDFData } from './PilotRunReportPDF';

// ─── Helpers ────────────────────────────────────────────────────────────────
const NILL = 'Nill';

const formatDate = (raw?: string | null) => {
  if (!raw) return NILL;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '.');
};

const val = (v: any): string => {
  if (v === null || v === undefined) return NILL;
  const str = String(v).trim();
  return str.length === 0 ? NILL : str;
};

// ─── Brand ──────────────────────────────────────────────────────────────────
const BORDER = '#000000';

// ─── Styles (aligned with MigrosFinalInspectionReportPDF) ────────────────────
const s = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: '#000000',
    backgroundColor: '#ffffff',
    paddingHorizontal: 32,
    paddingTop: 18,
    paddingBottom: 50,
  },

  topHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 6 },
  logoImg: { width: 140, height: 26, objectFit: 'contain' },
  subTitle: { fontSize: 8.5, color: '#000000', marginTop: 6, marginBottom: 10 },

  bigSectionTitle: { fontSize: 22, fontFamily: 'Helvetica-Bold', color: '#000000', marginVertical: 12, marginLeft: 18 },

  stageRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 },

  // Product picture row
  pictureRow: { flexDirection: 'row', borderWidth: 1, borderColor: BORDER, marginBottom: 0 },
  pictureCell: { flex: 1, borderRightWidth: 1, borderRightColor: BORDER, padding: 4 },
  pictureCellLast: { flex: 1, padding: 4 },
  pictureHeader: { backgroundColor: '#ffffff', padding: 3, fontSize: 9, fontFamily: 'Helvetica-Bold' },
  pictureImg: { width: '100%', height: 150, objectFit: 'contain' },
  pictureEmpty: { width: '100%', height: 150, alignItems: 'center', justifyContent: 'center' },
  pictureEmptyText: { fontSize: 9, color: '#888888', fontStyle: 'italic' },

  // Info table
  infoRow: { flexDirection: 'row', borderTopWidth: 1, borderTopColor: BORDER, minHeight: 20 },
  infoLabelCell: { width: 180, padding: 4, paddingHorizontal: 6, borderRightWidth: 1, borderRightColor: BORDER, justifyContent: 'center' },
  infoLabelText: { fontSize: 9 },
  infoValueCell: { flex: 1, padding: 4, paddingHorizontal: 6, justifyContent: 'center' },
  infoValueText: { fontSize: 9, fontFamily: 'Helvetica-Bold' },

  // Inspection Result row
  resultHeaderRow: { flexDirection: 'row', borderWidth: 1, borderColor: BORDER, marginTop: 14 },
  resultHeaderLabel: { flex: 1, padding: 4, paddingHorizontal: 6, borderRightWidth: 1, borderRightColor: BORDER, justifyContent: 'center' },
  resultHeaderLabelText: { fontSize: 9, fontFamily: 'Helvetica-Bold' },
  resultHeaderCheckCell: { flex: 1, padding: 4, paddingHorizontal: 6, borderRightWidth: 1, borderRightColor: BORDER, justifyContent: 'center' },
  resultHeaderCheckCellLast: { flex: 1, padding: 4, paddingHorizontal: 6, justifyContent: 'center' },

  // Signature blocks
  sigRow: { flexDirection: 'row', borderWidth: 1, borderColor: BORDER, marginTop: 14 },
  sigCell: { flex: 1, padding: 6, borderRightWidth: 1, borderRightColor: BORDER, minHeight: 70 },
  sigCellLast: { flex: 1, padding: 6, minHeight: 70 },
  sigTitle: { fontSize: 9, fontFamily: 'Helvetica-Bold', marginBottom: 4 },
  sigField: { fontSize: 8.5, marginBottom: 2 },
  sigImage: { width: 90, height: 32, objectFit: 'contain', marginTop: 2 },

  sectionTitle: { fontSize: 10, fontFamily: 'Helvetica-Bold', marginTop: 12, marginBottom: 6, textDecoration: 'underline' },

  // Generic data table
  dataTable: { borderWidth: 1, borderColor: BORDER, marginBottom: 6 },
  dataHeadRow: { flexDirection: 'row', backgroundColor: '#f0f0f0', borderBottomWidth: 1, borderBottomColor: BORDER },
  dataBodyRow: { flexDirection: 'row', borderTopWidth: 0.5, borderTopColor: BORDER, minHeight: 20 },
  dataCell: { flex: 1, padding: 4, paddingHorizontal: 5, borderRightWidth: 0.5, borderRightColor: BORDER, justifyContent: 'center' },
  dataCellLast: { flex: 1, padding: 4, paddingHorizontal: 5, justifyContent: 'center' },
  dataCellText: { fontSize: 8.5 },
  dataCellTextBold: { fontSize: 8.5, fontFamily: 'Helvetica-Bold' },
  dataCellTextCenter: { fontSize: 8.5, textAlign: 'center' },
  dataCellTextHead: { fontSize: 8.5, fontFamily: 'Helvetica-Bold', textAlign: 'center' },

  // Checkbox
  checkboxBoxLg: { width: 11, height: 11, borderWidth: 0.7, borderColor: BORDER, alignItems: 'center', justifyContent: 'center' },
  checkboxMarkLg: { fontSize: 9, fontFamily: 'Helvetica-Bold', lineHeight: 1 },
  checkboxLabel: { fontSize: 9, marginLeft: 4 },
  checkboxItem: { flexDirection: 'row', alignItems: 'center', marginRight: 8 },

  // Remarks block
  remarksBlock: { borderWidth: 1, borderColor: BORDER, padding: 6, minHeight: 26, marginBottom: 6 },
  remarksLabel: { fontSize: 9, fontFamily: 'Helvetica-Bold', marginBottom: 4 },

  // Photos grid
  photoGrid: { flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginBottom: 10 },
  photoTile: { width: 162 },
  photoImg: { width: 162, height: 160, objectFit: 'cover' },
  photoCaption: { fontSize: 7.5, color: '#444', marginTop: 2 },

  // Footer
  pageFooter: {
    position: 'absolute', bottom: 14, left: 32, right: 32,
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    borderTopWidth: 0.5, borderTopColor: '#cccccc', paddingTop: 4,
  },
  footerLeft: { fontSize: 7.5, color: '#555555', flex: 1 },
  footerCenter: { fontSize: 7.5, color: '#555555', textAlign: 'center', flex: 1 },
  footerRight: { fontSize: 7.5, color: '#555555', textAlign: 'right', flex: 1 },
});

// ─── Sub-components ─────────────────────────────────────────────────────────
const PageTop = ({ logoSrc }: { logoSrc: string }) => (
  <>
    <View style={s.topHeader} fixed>
      <Image src={logoSrc} style={s.logoImg} />
    </View>
    <Text style={s.subTitle} fixed>FINALRANDOMINSPECTION(TEXTILE/HOMETEXTILE)Version-1</Text>
  </>
);

const PageFooter = () => (
  <View style={s.pageFooter} fixed>
    <Text style={s.footerLeft}>MIGROS Asia</Text>
    <Text
      style={s.footerCenter}
      render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
    />
    <Text style={s.footerRight}>Pilot Run Inspection</Text>
  </View>
);

const Checkbox = ({ checked, label }: { checked?: boolean; label: string }) => (
  <View style={s.checkboxItem}>
    <View style={s.checkboxBoxLg}>
      {checked ? <Text style={s.checkboxMarkLg}>X</Text> : null}
    </View>
    <Text style={s.checkboxLabel}>{label}</Text>
  </View>
);

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <View style={s.infoRow}>
    <View style={s.infoLabelCell}>
      <Text style={s.infoLabelText}>{label}</Text>
    </View>
    <View style={s.infoValueCell}>
      <Text style={s.infoValueText}>{value}</Text>
    </View>
  </View>
);

// ─── Cover photos ────────────────────────────────────────────────────────────
const findCoverPhotos = (inspection: PilotRunPDFData) => {
  const all = (inspection.photos || []).filter((p) => p.image_url);
  const findByKey = (...keys: string[]) =>
    all.find((p) => keys.some((k) => (p.label || '').toLowerCase().includes(k)));
  const front = findByKey('front');
  const back = findByKey('back');
  return {
    front: front?.image_url || all[0]?.image_url || null,
    back: back?.image_url || all[1]?.image_url || null,
  };
};

// ─── Main PDF Document ──────────────────────────────────────────────────────
const MigrosPilotRunReportPDF = ({ inspection, logoDataUrl }: { inspection: PilotRunPDFData; logoDataUrl?: string }) => {
  const logoSrc = logoDataUrl || migrosLogoDataUrl;
  const result = (inspection.result || 'PENDING').toUpperCase();
  const isPass = result === 'PASS' || result === 'COMPLETED';
  const isFail = result === 'FAIL';
  const inspectorName = inspection.inspector?.name || inspection.inspector_name || NILL;

  const articleNo = val(inspection.sku_number || inspection.factory_code);
  const { front: frontPhoto, back: backPhoto } = findCoverPhotos(inspection);

  const compositionText = (inspection.compositions || [])
    .map((c) => `${val(c.percentage)}% ${val(c.material)}`)
    .filter((t) => t !== 'Nill% Nill')
    .join(', ');

  const processes = (inspection.processes || []).map((p) => p?.name).filter(Boolean) as string[];
  const screens = inspection.screens || [];
  const lifetimes = inspection.lifetimes || [];

  // Collect all photos for the picture pages
  const allPhotos: { url: string; label?: string }[] = [];
  for (const p of inspection.photos || []) {
    if (p.image_url) allPhotos.push({ url: p.image_url, label: p.label || '' });
  }
  for (const c of inspection.ctqImages || []) {
    if (c.image_url) allPhotos.push({ url: c.image_url, label: 'CTQ' });
  }

  const dim = (v: any, unit?: string | null) => (v ? `${v} ${val(unit) === NILL ? '' : unit}`.trim() : NILL);

  return (
    <Document title={`Migros Pilot Run Report - ${inspection.sku_number || inspection.id}`}>

      {/* ═══ PAGE 1 — COVER ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop logoSrc={logoSrc} />

        <View style={s.stageRow}>
          <Checkbox label="Factory Self Inspection" />
          <Checkbox label="Pilot Run Inspection" checked />
          <Checkbox label="Re-Inspection" />
        </View>

        {/* Product Front / Back picture row */}
        <View style={s.pictureRow}>
          <View style={s.pictureCell}>
            <Text style={s.pictureHeader}>PRODUCT FRONT PICTURE</Text>
            {frontPhoto ? (
              <Image src={frontPhoto} style={s.pictureImg} />
            ) : (
              <View style={s.pictureEmpty}><Text style={s.pictureEmptyText}>{NILL}</Text></View>
            )}
          </View>
          <View style={s.pictureCellLast}>
            <Text style={s.pictureHeader}>PRODUCT BACK PICTURE</Text>
            {backPhoto ? (
              <Image src={backPhoto} style={s.pictureImg} />
            ) : (
              <View style={s.pictureEmpty}><Text style={s.pictureEmptyText}>{NILL}</Text></View>
            )}
          </View>
        </View>

        {/* Info table */}
        <View style={{ borderWidth: 1, borderColor: BORDER, borderTopWidth: 0 }}>
          <InfoRow label="Inspection Date:" value={formatDate(inspection.inspection_date || inspection.created_at)} />
          <InfoRow label="SAP No./Supplier Name:" value={val(inspection.buyer_name)} />
          <InfoRow label="Basis:" value={val(inspection.basis)} />
          <InfoRow label="Contract/ Order no.:" value={val(inspection.ref_number)} />
          <InfoRow label="Product Description:" value={val(inspection.product_desc)} />
          <InfoRow label="Article No.:" value={articleNo} />
          <InfoRow label="Vendor Product Code:" value={val(inspection.factory_code)} />
          <InfoRow label="Order Quantity:" value={inspection.total_qty ? `${inspection.total_qty} PCS` : NILL} />
          <InfoRow label="Season:" value={val(inspection.season)} />
          <InfoRow label="Composition:" value={compositionText || NILL} />
        </View>

        {/* Inspection Result Pass/Fail row */}
        <View style={s.resultHeaderRow}>
          <View style={s.resultHeaderLabel}>
            <Text style={s.resultHeaderLabelText}>INSPECTIONRESULT</Text>
          </View>
          <View style={s.resultHeaderCheckCell}>
            <Checkbox checked={isPass} label="PASSED" />
          </View>
          <View style={s.resultHeaderCheckCellLast}>
            <Checkbox checked={isFail} label="FAILED" />
          </View>
        </View>

        {/* Signature blocks */}
        <View style={s.sigRow}>
          <View style={s.sigCell}>
            <Text style={s.sigTitle}>INSPECTOR</Text>
            <Text style={s.sigField}>Name: {inspectorName}</Text>
            <Text style={s.sigField}>Signature:</Text>
            {inspection.inspector_signature ? (
              <Image src={inspection.inspector_signature} style={s.sigImage} />
            ) : null}
          </View>
          <View style={s.sigCellLast}>
            <Text style={s.sigTitle}>FACTORY REPRESENTATIVE</Text>
            <Text style={s.sigField}>Name:</Text>
            <Text style={s.sigField}>Signature:</Text>
            {inspection.factory_signature ? (
              <Image src={inspection.factory_signature} style={s.sigImage} />
            ) : null}
          </View>
        </View>

        <PageFooter />
      </Page>

      {/* ═══ PAGE 2 — Dimensions + Production Process ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop logoSrc={logoSrc} />

        <Text style={s.sectionTitle}>Part1- Product specification and dimensions</Text>
        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Length</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Width</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Height</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>Weight</Text></View>
          </View>
          <View style={s.dataBodyRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{dim(inspection.length, inspection.length_unit)}</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{dim(inspection.width, inspection.width_unit)}</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{dim(inspection.height, inspection.height_unit)}</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{dim(inspection.weight, inspection.weight_unit)}</Text></View>
          </View>
        </View>

        <Text style={s.sectionTitle}>Part2- Production process</Text>
        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>Process Steps</Text></View>
          </View>
          {processes.length > 0 ? (
            processes.map((p, i) => (
              <View key={i} style={s.dataBodyRow}>
                <View style={s.dataCellLast}><Text style={s.dataCellText}>{`${i + 1}. ${p}`}</Text></View>
              </View>
            ))
          ) : (
            <View style={s.dataBodyRow}>
              <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
            </View>
          )}
        </View>

        <Text style={s.sectionTitle}>Number of Screens</Text>
        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Color</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Number</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>Unit</Text></View>
          </View>
          {screens.length > 0 ? (
            screens.map((r, i) => (
              <View key={i} style={s.dataBodyRow}>
                <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(r.color)}</Text></View>
                <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(r.count)}</Text></View>
                <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{val(r.unit)}</Text></View>
              </View>
            ))
          ) : (
            <View style={s.dataBodyRow}>
              <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
              <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
              <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
            </View>
          )}
        </View>

        <Text style={s.sectionTitle}>Lifetime</Text>
        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Color</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Number</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>Unit</Text></View>
          </View>
          {lifetimes.length > 0 ? (
            lifetimes.map((r, i) => (
              <View key={i} style={s.dataBodyRow}>
                <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(r.color)}</Text></View>
                <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(r.count)}</Text></View>
                <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{val(r.unit)}</Text></View>
              </View>
            ))
          ) : (
            <View style={s.dataBodyRow}>
              <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
              <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
              <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
            </View>
          )}
        </View>

        <PageFooter />
      </Page>

      {/* ═══ PAGE 3 — Evaluation ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop logoSrc={logoSrc} />

        <Text style={s.sectionTitle}>Part3- Evaluation and findings</Text>

        <View style={s.remarksBlock}>
          <Text style={s.remarksLabel}>CTQ Remarks:</Text>
          <Text style={{ fontSize: 9 }}>{val(inspection.ctq_remarks)}</Text>
        </View>

        <View style={s.remarksBlock}>
          <Text style={s.remarksLabel}>Overall Evaluation:</Text>
          <Text style={{ fontSize: 9 }}>{val(inspection.evaluation)}</Text>
        </View>

        <View style={s.remarksBlock}>
          <Text style={s.remarksLabel}>Corrective Actions:</Text>
          <Text style={{ fontSize: 9 }}>{val(inspection.corrective_actions)}</Text>
        </View>

        <PageFooter />
      </Page>

      {/* ═══ PAGE 4+ — Pictures ═══ */}
      {allPhotos.length === 0 ? (
        <Page size="A4" style={s.page}>
          <PageTop logoSrc={logoSrc} />
          <Text style={s.bigSectionTitle}>PICTURES</Text>
          <Text style={{ fontSize: 9, marginLeft: 18, color: '#444' }}>{NILL}</Text>
          <PageFooter />
        </Page>
      ) : (
        (() => {
          const pages: { url: string; label?: string }[][] = [];
          for (let i = 0; i < allPhotos.length; i += 6) pages.push(allPhotos.slice(i, i + 6));
          return pages.map((chunk, pi) => (
            <Page key={pi} size="A4" style={s.page}>
              <PageTop logoSrc={logoSrc} />
              {pi === 0 ? <Text style={s.bigSectionTitle}>PICTURES</Text> : null}
              <View style={s.photoGrid}>
                {chunk.map((ph, i) => (
                  <View key={i} style={s.photoTile}>
                    <Image src={ph.url} style={s.photoImg} />
                    {ph.label ? <Text style={s.photoCaption}>{ph.label}</Text> : null}
                  </View>
                ))}
              </View>
              <PageFooter />
            </Page>
          ));
        })()
      )}

    </Document>
  );
};

export default MigrosPilotRunReportPDF;
